import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useGoBack } from "../../shared/phone/history";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { ActionSheet } from "../../shared/phone/Sheet";
import { PnRow, SaveBar, Segmented } from "../../shared/phone/Row";
import type { PartnerAuthIdentity } from "../../shared/types";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { type Mode, useTheme } from "../context/ThemeContext";
import { initialsOf } from "../lib/format";
import { Icon } from "../components/phone/Icon";
import { PoweredBy } from "../components/phone/parts";
import { toast } from "../components/phone/Toast";

type Note = { ok: boolean; text: string } | null;

/** One field as a row: its name on the left, the value typed on the right. */
function FieldRow({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="pn-irow">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Account on a phone and an iPad: the photo and name at the top, name and phone edited in place with a
 * save bar, the email read only, then Change password, All programs, Appearance and Log out (with a
 * confirm).
 */
export function AccountNative({
  partner,
  firstName,
  lastName,
  phone,
  onFirst,
  onLast,
  onPhone,
  dirty,
  saving,
  msg,
  onSave,
  onDiscard,
  avatarBusy,
  onPhoto,
  onRemovePhoto,
}: {
  partner: PartnerAuthIdentity;
  firstName: string;
  lastName: string;
  phone: string;
  onFirst: (v: string) => void;
  onLast: (v: string) => void;
  onPhone: (v: string) => void;
  dirty: boolean;
  saving: boolean;
  msg: Note;
  onSave: (e: FormEvent) => void;
  onDiscard: () => void;
  avatarBusy: boolean;
  onPhoto: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemovePhoto: () => void;
}) {
  const { session, logout } = usePartnerAuth();
  const { mode, setMode } = useTheme();
  const form = useRef<HTMLFormElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [photoMenu, setPhotoMenu] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);

  useEffect(() => {
    if (msg?.ok) toast("Saved");
  }, [msg]);

  const name = [partner.firstName, partner.lastName].join(" ").trim() || "Partner";
  const programs = session?.programs.length ?? 0;

  return (
    <div className="pn-screen po-acct">
      <PhoneNav large title="Account" />
      <div className="pn-stack">
        <section className="po-me">
          <button type="button" className="po-me-ph" onClick={() => setPhotoMenu(true)} disabled={avatarBusy} aria-label="Change your photo">
            {partner.avatarUrl ? <img src={partner.avatarUrl} alt="" /> : <span className="po-me-mono">{initialsOf(name)}</span>}
            <span className="po-cam">{avatarBusy ? <i className="po-spin" /> : <Icon name="camera" />}</span>
          </button>
          <b>{name}</b>
          <span>{partner.email}</span>
          <input ref={fileRef} type="file" accept="image/*" className="po-file" onChange={onPhoto} tabIndex={-1} aria-hidden="true" />
        </section>

        <section>
          <form ref={form} className="pn-grp" onSubmit={onSave} autoComplete="on">
            <FieldRow id="po-a-first" label="First name">
              <input id="po-a-first" name="given-name" autoComplete="given-name" autoCapitalize="words" value={firstName} onChange={(e) => onFirst(e.target.value)} />
            </FieldRow>
            <FieldRow id="po-a-last" label="Last name">
              <input id="po-a-last" name="family-name" autoComplete="family-name" autoCapitalize="words" value={lastName} onChange={(e) => onLast(e.target.value)} />
            </FieldRow>
            <FieldRow id="po-a-phone" label="Phone">
              <input id="po-a-phone" name="tel" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => onPhone(e.target.value)} placeholder="Add a phone" />
            </FieldRow>
          </form>
          {msg && !msg.ok ? (
            <p className="pn-note po-err" role="alert">
              {msg.text}
            </p>
          ) : null}
        </section>

        <section>
          <div className="pn-grp">
            <FieldRow id="po-a-email" label="Email">
              <input id="po-a-email" type="email" value={partner.email} readOnly aria-readonly="true" />
            </FieldRow>
          </div>
          <p className="pn-note">Your email is how you sign in. To change it, ask the program owner.</p>
        </section>

        <div className="pn-grp">
          <PnRow to="/profile/password" icon={<Icon name="lock" />} iconTone="plain" title="Change password" />
          <PnRow to="/profile/programs" icon={<Icon name="grid" />} iconTone="plain" title="All programs" value={String(programs)} />
        </div>

        <section>
          <div className="pn-cap">Appearance</div>
          <div className="po-look">
            <Segmented<Mode>
              label="Appearance"
              value={mode}
              onChange={setMode}
              options={[
                {
                  value: "light",
                  label: (
                    <>
                      <Icon name="sun" />
                      Light
                    </>
                  ),
                },
                {
                  value: "dark",
                  label: (
                    <>
                      <Icon name="moon" />
                      Dark
                    </>
                  ),
                },
              ]}
            />
          </div>
        </section>

        <div className="pn-grp">
          <button type="button" className="pn-row danger" onClick={() => setConfirmOut(true)}>
            <span className="m">
              <span className="t">Log out</span>
            </span>
          </button>
        </div>
        <PoweredBy />
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={() => form.current?.requestSubmit()} onDiscard={onDiscard} />

      <ActionSheet
        open={photoMenu}
        onClose={() => setPhotoMenu(false)}
        title="Your photo"
        actions={[
          { label: "Choose a photo", onSelect: () => fileRef.current?.click() },
          ...(partner.avatarUrl ? [{ label: "Remove photo", danger: true, onSelect: onRemovePhoto }] : []),
        ]}
      />
      <ActionSheet
        open={confirmOut}
        onClose={() => setConfirmOut(false)}
        message="You can log back in with your email and password."
        actions={[
          {
            label: "Log out",
            danger: true,
            onSelect: () => void logout().catch(() => {}),
          },
        ]}
      />
    </div>
  );
}

/** Change password, its own screen, with the phone's password AutoFill. */
export function PasswordNative({
  currentPw,
  newPw,
  confirmPw,
  onCurrent,
  onNew,
  onConfirm,
  busy,
  msg,
  onSubmit,
}: {
  currentPw: string;
  newPw: string;
  confirmPw: string;
  onCurrent: (v: string) => void;
  onNew: (v: string) => void;
  onConfirm: (v: string) => void;
  busy: boolean;
  msg: Note;
  onSubmit: (e: FormEvent) => void;
}) {
  const goBack = useGoBack();
  const { session } = usePartnerAuth();

  useEffect(() => {
    if (!msg?.ok) return;
    toast("Password updated");
    goBack("/profile");
  }, [msg, goBack]);

  return (
    <div className="pn-screen">
      <PhoneNav large title="Change password" back={{ to: "/profile", label: "Account" }} />
      <div className="pn-stack">
        <form id="po-pw" className="pn-grp po-pw" onSubmit={onSubmit}>
          <input type="email" name="username" autoComplete="username" value={session?.partner.email ?? ""} readOnly hidden />
          <div className="pn-irow col">
            <label htmlFor="po-pw0">Current password</label>
            <input id="po-pw0" type="password" autoComplete="current-password" value={currentPw} onChange={(e) => onCurrent(e.target.value)} />
          </div>
          <div className="pn-irow col">
            <label htmlFor="po-pw1">New password</label>
            <input id="po-pw1" type="password" autoComplete="new-password" placeholder="At least 8 characters" value={newPw} onChange={(e) => onNew(e.target.value)} />
          </div>
          <div className="pn-irow col">
            <label htmlFor="po-pw2">Confirm new password</label>
            <input id="po-pw2" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => onConfirm(e.target.value)} enterKeyHint="done" />
          </div>
        </form>
        {msg && !msg.ok ? (
          <p className="pn-note po-err" role="alert">
            {msg.text}
          </p>
        ) : null}
      </div>
      <div className="pn-foot">
        <button type="submit" form="po-pw" className="pn-btn pri wide" disabled={busy}>
          {busy ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}
